import { useMutation } from '@apollo/client';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { READ_EXAMPLE } from '../../../graphql/queries';
import {
  ADD_FILE_DOCUMENT,
  ADD_LINK_DOCUMENT,
  EDIT_EXAMPLE,
  EDIT_FILE_DOCUMENT,
  EDIT_LINK_DOCUMENT,
} from '../../../graphql/mutations';
import AggregateErrors from '../../../utils/AggregateErrors';
import { ReturnLayout } from '../../../Components/ReturnLayout';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Alert from 'react-bootstrap/Alert';
import ExampleForm, {
  type ExampleFormData,
} from '../../../Components/ExampleForm';
import axios from 'axios';
import {
  type ReadExampleQuery,
  type DocumentSource,
} from '../../../__generated__/graphql';
import ContentLoader from '../../../Components/ContentLoader';
import { type StandardExample } from '../../../graphql/types';

/**
 * Ce coposant permet d'afficher le formulaire nécessaire pour la
 * modification d'un exemple associé à un indicateur.
 *
 * @returns Le composant rendu par React.
 */
export default function EditExample(): React.ReactElement {
  // Paramètres de requêtes
  const { id, exampleId } = useParams();

  // Variables d'états
  const [isSourceDifferent, setSourceDifferent] = useState(false);
  const [hasNewFile, setNewFile] = useState(false);
  const [isDocumentUploading, setDocumentUploading] = useState(false);
  const [formData, setFormData] = useState<ExampleFormData | undefined>(
    undefined
  );
  const [example, setExample] = useState<StandardExample | undefined>(
    undefined
  );
  const [uploadDocumentError, setUploadDocumentError] = useState('');

  // navigation
  const navigate = useNavigate();

  const [
    editExample,
    {
      loading: editExampleLoading,
      data: editExampleData,
      error: editExampleError,
    },
  ] = useMutation(EDIT_EXAMPLE);
  const [
    editFileDocument,
    {
      loading: editFileDocumentLoading,
      data: editFileDocumentData,
      error: editFileDocumentError,
    },
  ] = useMutation(EDIT_FILE_DOCUMENT);
  const [
    editLinkDocument,
    {
      loading: editLinkDocumentLoading,
      data: editLinkDocumentData,
      error: editLinkDocumentError,
    },
  ] = useMutation(EDIT_LINK_DOCUMENT);
  const [
    addFileDocument,
    {
      loading: addFileDocumentLoading,
      data: addFileDocumentData,
      error: addFileDocumentError,
    },
  ] = useMutation(ADD_FILE_DOCUMENT);
  const [
    addLinkDocument,
    {
      loading: addLinkDocumentLoading,
      data: addLinkDocumentData,
      error: addLinkDocumentError,
    },
  ] = useMutation(ADD_LINK_DOCUMENT);

  // données de rendu
  let errors = AggregateErrors(
    editExampleError,
    editExampleData?.editExample.errors
  );
  errors = AggregateErrors(
    editFileDocumentError,
    editFileDocumentData?.editFileDocument.errors,
    errors
  );
  errors = AggregateErrors(
    editLinkDocumentError,
    editLinkDocumentData?.editLinkDocument.errors,
    errors
  );
  errors = AggregateErrors(
    addFileDocumentError,
    addFileDocumentData?.addFileDocument.errors,
    errors
  );
  errors = AggregateErrors(
    addLinkDocumentError,
    addLinkDocumentData?.addLinkDocument.errors,
    errors
  );
  if (uploadDocumentError !== '') {
    errors.push(uploadDocumentError);
  }

  const isComplete =
    editExampleData !== undefined ||
    editFileDocumentData !== undefined ||
    editLinkDocumentData !== undefined ||
    addFileDocumentData !== undefined ||
    addLinkDocumentData !== undefined ||
    editExampleError !== undefined ||
    editFileDocumentError !== undefined ||
    editLinkDocumentError !== undefined ||
    addFileDocumentError !== undefined ||
    addLinkDocumentError !== undefined;

  const isLoading =
    editExampleLoading ||
    editFileDocumentLoading ||
    editLinkDocumentLoading ||
    addFileDocumentLoading ||
    addLinkDocumentLoading ||
    isDocumentUploading;

  const documentCanBeEdited =
    example?.document.canBeEdited ?? (isSourceDifferent || hasNewFile);

  // le `useEffect`, combiné avec la variable d'état, empêche de mettre
  // à jour les données du formulaire lorsque ce dernier communique avec nous
  // pour autre choses que la soumission de ce dernier. En effet, quand le
  // type change, les données de formulaires étaient recrées, ce qui forçait
  // le formulaire à ne pas tenir compte de l'ancienne valeur. Le code ici
  // présent empêche cette faute de survenir.
  useEffect(() => {
    setFormData({
      fileName: example?.document.content,
      url: example?.document.url,
      title: example?.document.title,
      abstract: example?.document.abstract,
      authors: example?.document.authors,
      publicationYear: example?.document.publicationYear,
      type: example?.document.source,
      remark: example?.remark,
    });
  }, [example]);

  // Fonctions utilitaires
  /**
   * Envoie la requête de mofication d'exemple à l'API.
   *
   * Si la requête est réussie, le composant navigue vers les détails d'un
   * indicateur.
   *
   * @param remark Les nouvelles remarques, si applicable.
   * @param documentId Le nouveau document, si applicable.
   */
  const sendExample = (remark?: string, documentId?: string): void => {
    if (example !== undefined) {
      editExample({
        variables: {
          id: example.id,
          remark,
          documentId,
        },
      }).then(editExampleResult => {
        if (
          !editExampleResult.errors &&
          !editExampleResult.data?.editExample.errors
        ) {
          navigate(`/indicateurs/${id}`);
        }
      });
    }
  };

  /**
   * Téléverse le document dans l'API.
   *
   * Une fois la fonction réussie, elle envoie l'exemple à l'API.
   *
   * @param uploadURL Le lien de téléversement.
   *
   * @param document Le document à téléverser.
   *
   * @param remark Les nouvelles remarques, si applicables.
   * Ce paramètre est transféré à l'envoi d'exemples.
   */
  const uploadDocument = (
    uploadURL: string,
    document: File,
    remark?: string
  ): void => {
    setDocumentUploading(true);
    axios
      .post(uploadURL, document, {
        headers: {
          'Content-Type': 'application/pdf',
        },
        withCredentials: true,
      })
      .then(response => {
        if (response.data.id !== null) {
          // Ce code ne sera jamais atteint puisqu'on perd trace du document
          // avant de vérifier si on peut le supprimer. Il faut une requête qui nous
          // permet d'obtenir les documents avant de pouvoir supprimer le superflu.
          // Le code a été commenté pour éviter des couvertures de tests non
          // atteignables.
          // if (example?.document.canBeDeleted) {
          //   deleteLinkDocument({
          //     variables: {
          //       id: example.document.id,
          //     },
          //   });
          // }

          sendExample(remark, response.data.id);
        }
      })
      .catch(error => {
        if ((error.response?.status ?? -1) === 409) {
          sendExample(remark, error.response?.data.ressourceId);
        } else if (error.response !== undefined) {
          setUploadDocumentError(error.response.data);
        } else if (error.request !== undefined) {
          setUploadDocumentError(error.request);
        } else {
          setUploadDocumentError(error.message);
        }
      })
      .finally(() => {
        setDocumentUploading(false);
      });
  };

  /**
   * Envoie à l'API pour la création d'un nouveau document de type fichier.
   *
   * Cette fonction est la première étape de la chaine. La seconde consiste
   * à téléverser le document tandis que la troisième consiste à modifier
   * l'exemple.
   *
   * @param fileName Le nom du fichier associé au nouveau document.
   *
   * @param title Le titre associé au nouveau document.
   *
   * @param abstract Le résumé associé au nouveau document.
   *
   * @param document Le fichier associé au nouveau document.
   *
   * @param remark La nouvelle remarque, si applicable. Celle-ci est transféré
   * aux étapes subséquentes.
   *
   * @param authors La liste des auteur(rice)s associée au nouveau document.
   *
   * @param publicationYear L'année de publication associé au nouveau document.
   */
  const sendFileDocument = (
    fileName: string,
    title: string,
    abstract: string,
    document: File,
    remark?: string,
    authors?: string[] | null,
    publicationYear?: number | null
  ): void => {
    addFileDocument({
      variables: {
        fileName,
        title,
        abstract,
        authors,
        publicationYear,
      },
    }).then(addFileDocumentResult => {
      if (
        !addFileDocumentResult.errors &&
        !addFileDocumentResult.data?.addFileDocument.errors
      ) {
        uploadDocument(
          addFileDocumentResult.data?.addFileDocument.uploadToken?.uploadURL ??
            '',
          document,
          remark
        );
      }
    });
  };

  /**
   * Cette fonction permet de contenir toute la logique associée à la modification
   * d'un document de type lien.
   *
   * @param data Les données du formulaire.
   */
  const handleLinkDocumentEdition = (data: ExampleFormData): void => {
    // On modifie le document existant et l'exemple actuel.
    if (data.type === 'LINK') {
      editLinkDocument({
        variables: {
          id: example?.document.id ?? '',
          url: data.url,
          title: data.title,
          abstract: data.abstract,
          authors: data.authors,
          publicationYear: data.publicationYear,
        },
      }).then(result => {
        if (!result.errors && !result.data?.editLinkDocument.errors) {
          sendExample(data.remark);
        }
      });
    } else if (
      data.fileName !== undefined &&
      data.fileName !== '' &&
      data.document !== undefined &&
      data.document !== null
    ) {
      // On créé un nouveau document de type fichier.
      // On téléverse le nouveau document.
      // On récupère l'enregistrement du document qui a été délié à l'exemple.
      // On regarde si le document délié. peut être supprimé, auquel cas on le supprime.
      // On enregistre les modifications à l'exemple.
      sendFileDocument(
        data.fileName,
        data.title ?? example?.document.title ?? '',
        data.abstract ?? example?.document.abstract ?? '',
        data.document,
        data.remark,
        data.authors ?? example?.document.authors,
        data.publicationYear ?? example?.document.publicationYear
      );
    }
  };

  /**
   * Cette fonction permet de contenir toute la logique associée à la modification
   * d'un document de type fichier.
   *
   * @param data Les données du formulaire.
   */
  const handleFileDocumentEdition = (data: ExampleFormData): void => {
    if (
      data.fileName !== undefined &&
      data.fileName !== '' &&
      data.document !== undefined &&
      data.document !== null
    ) {
      // On créé un nouveau document de type fichier.
      // On téléverse le nouveau document
      // On modifie l'exemple
      // On récupère l'enregistrement du document qui a été délié à l'exemple.
      // On regarde si le document délié peut être supprimé, auquel cas on le supprime.
      // C'est essentiellement les mêmes étapes que le changement lien -> fichier.
      sendFileDocument(
        data.fileName,
        data.title ?? example?.document.title ?? '',
        data.abstract ?? example?.document.abstract ?? '',
        data.document,
        data.remark,
        data.authors ?? example?.document.authors,
        data.publicationYear ?? example?.document.publicationYear
      );
    } else if (data.type === 'LINK') {
      // On créé un nouveau document de type lien
      // On modifie l'exemple
      // On récupère l'enregistrement du document qui a été délié à l'exemple.
      // On regarde si le document délié peut être supprimé, auquel cas on le supprime.
      if (data.url !== undefined && data.url !== '') {
        addLinkDocument({
          variables: {
            title: data.title ?? example?.document.title ?? '',
            abstract: data.abstract ?? example?.document.abstract ?? '',
            url: data.url,
            publicationYear:
              data.publicationYear ?? example?.document.publicationYear,
            authors: data.authors ?? example?.document.authors,
          },
        }).then(result => {
          if (!result.errors && !result.data?.addLinkDocument.errors) {
            // Ce code ne sera jamais atteint puisqu'on perd trace du document
            // avant de vérifier si on peut le supprimer. Il faut une requête qui nous
            // permet d'obtenir les documents avant de pouvoir supprimer le superflu.
            // Le code a été commenté pour éviter des couvertures de tests non
            // atteignables.
            // if (example?.document.canBeDeleted) {
            //   deleteFileDocument({
            //     variables: {
            //       id: example.document.id,
            //     },
            //   });
            // }

            sendExample(data.remark, result.data?.addLinkDocument.document?.id);
          }
        });
      }
    } else {
      // On modifie le document
      // On modifie l'exemple
      editFileDocument({
        variables: {
          id: example?.document.id ?? '',
          fileName: data.fileName,
          title: data.title,
          abstract: data.abstract,
          authors: data.authors,
          publicationYear: data.publicationYear,
        },
      }).then(result => {
        if (!result.errors && !result.data?.editFileDocument.errors) {
          sendExample(data.remark);
        }
      });
    }
  };

  // Fonctions de rappel
  const onDocumentSourceChanged = useCallback(
    (newSource: DocumentSource): void => {
      setSourceDifferent(newSource !== example?.document.source);
    },
    []
  );

  const onFileChanged = useCallback((): void => {
    setNewFile(true);
  }, []);

  const onFormSubmit = useCallback(
    (
      event: React.FormEvent<HTMLFormElement>,
      data: ExampleFormData,
      setValidated: React.Dispatch<React.SetStateAction<boolean>>
    ): void => {
      if (!isLoading && data.type !== undefined) {
        event.preventDefault();
        event.stopPropagation();

        if (example?.document.source === 'LINK') {
          handleLinkDocumentEdition(data);
        } else {
          handleFileDocumentEdition(data);
        }
      }
      setValidated(true);
    },
    [exampleId, example]
  );

  /**
   * Affiche le formulaire à l'écran.
   *
   * Cette fonction s'assure également que tous les éléments du formulaires soient
   * adéquatement chargées avant d'effectuer le rendu de la page.
   */
  const displayForm = useCallback(
    (data: ReadExampleQuery | undefined): React.ReactElement => {
      const example = data?.examples?.items?.at(0);
      if (!example) {
        // On retourne une erreur
        return (
          <Alert variant="danger">
            Aucun exemple avec l&apos;identifiant unique {exampleId} n&apos;a
            été trouvé.
          </Alert>
        );
      } else {
        setExample(example);
      }

      return (
        <>
          {!example?.document.canBeEdited && (
            <Row>
              <Col>
                <Alert variant="warming" dismissible>
                  Le document associé à cet exemple est utilisé par
                  d&apos;autres exemples. Leurs champs sont donc placés en
                  lecture seule.
                </Alert>
              </Col>
            </Row>
          )}
          <Row>
            <Col>
              <Alert variant="info" dismissible>
                Si le type de document est changé, un nouveau document sera
                créé. Pour un document avec un fichier, si un nouveau fichier
                est téléversé, un nouveau document sera créé.
              </Alert>
            </Col>
          </Row>

          <Row>
            <Col>
              <ExampleForm
                onFormSubmit={onFormSubmit}
                onDocumentSourceChanged={onDocumentSourceChanged}
                onFileChanged={onFileChanged}
                isComplete={isComplete}
                errors={errors}
                loading={isLoading}
                formData={formData}
                documentCanBeEdited={documentCanBeEdited}
              />
            </Col>
          </Row>
        </>
      );
    },
    [
      exampleId,
      example,
      isComplete,
      errors,
      isLoading,
      formData,
      documentCanBeEdited,
    ]
  );

  return (
    <ReturnLayout href={`/indicateurs/${id}`}>
      <Row>
        <Col>
          <h2>Modifier un exemple</h2>
        </Col>
      </Row>
      <ContentLoader
        query={READ_EXAMPLE}
        variables={{ exampleId: exampleId ?? '' }}
        data={displayForm}
      />
    </ReturnLayout>
  );
}
